import DropdownPicker from "frontend/ui-components/picker/dropdown-picker";
import { getPathPrefix } from "utils/getPathPrefix";
import type { MondayStyledDropdownPickerProps } from "./monday-styled-dropdown-picker.types";
import styles from "./monday-styled-dropdown-picker.module.css";
import useDebounceCallback from "frontend/hooks/use-debounce-callback";
import tracking from "frontend/tracking";

export function MondayStyledDropdownPicker<T>({
  options,
  selectedOption,
  onChange,
  isSelected,
  getTitle,
  renderOption,
  renderSelectedOption,
  placeholder = "Select...",
  disabled = false,
  isLoading = false,
  width = "156px",
  optionsWidth = "156px",
  enableSearch = false,
  searchPlaceholder = "Search...",
  maxHeight = "296px",
  customIcon,
  onFetchMore,
  loadingMore = false,
  isOptionDisabled,
  noResultsText,
  includeOverlay = false,
  scrollToSelected = true,
  useFloatingDropdown = false,
  portalId,
  trackingEventName,
  onSearch,
}: MondayStyledDropdownPickerProps<T>): React.ReactElement {
  const debouncedFetchMore = useDebounceCallback(() => onFetchMore?.(), 1_000);

  const defaultNoResultsText = (searchValue: string) => (
    <div className={styles.noResultsTextContainer}>
      No results matching <span className={styles.noResultsText}>{searchValue}</span>
    </div>
  );

  return (
    <DropdownPicker<T>
      options={options}
      titles={getTitle}
      optionRenderer={(option) => {
        const isDisabled = isOptionDisabled?.(option.option) ?? false;
        return {
          element: renderOption(option.option),
          enabled: !isDisabled,
        };
      }}
      onChange={(option) => {
        onChange(option);
        tracking.trackAnalyticsEvent(trackingEventName);
      }}
      isSelected={isSelected}
      enabled={!disabled && options.length > 0}
      scrollToSelected={scrollToSelected && selectedOption !== null}
      selectionHeaderRenderer={({ selectedOptions }) => {
        if (isLoading) {
          return <div className={styles.loadingMore}>Loading...</div>;
        }

        if (!selectedOption) {
          return <div className={styles.placeholder}>{placeholder}</div>;
        }

        if (renderSelectedOption) {
          return renderSelectedOption(selectedOption);
        }

        if (!selectedOptions || selectedOptions.length === 0) {
          return <div className={styles.placeholder}>{placeholder}</div>;
        }

        return <div className={styles.selectedText}>{getTitle(selectedOptions[0])}</div>;
      }}
      pickerCustomIcon={customIcon || getPathPrefix("/images/chevron-black.svg")}
      pickerCustomStyle={{
        borderRadius: "6px",
        height: "32px",
        width,
        padding: "0 12px 0 10px",
        border: "none",
        backgroundColor: "#F6F7FB",
      }}
      enableSearch={enableSearch}
      styles={{
        pickerParent: styles.pickerParent,
        rounded: true,
        searchContainer: enableSearch ? styles.searchContainer : undefined,
        optionClassName: styles.optionClassName,
      }}
      searchPlaceholder={searchPlaceholder}
      optionsContainerStyle={{
        fontSize: 16,
        color: "#113357",
        borderRadius: "6px",
        maxWidth: optionsWidth || width,
        gap: "5px",
        border: "none",
        paddingBottom: "0px",
      }}
      optionsPickerStyle={{
        maxHeight,
      }}
      onFetchMore={onFetchMore ? debouncedFetchMore : undefined}
      loadingMore={loadingMore}
      noResultsText={noResultsText || defaultNoResultsText}
      includeOverlay={includeOverlay}
      useFloatingDropdown={useFloatingDropdown}
      portalId={portalId}
      onSearch={onSearch}
    />
  );
}
